import React, { ComponentType, MouseEvent, useRef, useState } from 'react';
import moment from 'moment';
import { Nullable } from 'global';
import { useTheme } from '@mui/material/styles';
import { Calendar as BigCalendar, momentLocalizer, CalendarProps, EventPropGetter } from 'react-big-calendar';
import withDragAndDrop, { withDragAndDropProps } from 'react-big-calendar/lib/addons/dragAndDrop';

import { CalendarEvent } from './types';
import { getPriorityColor } from './CalendarEventCard';
import { CalendarWrapper } from './CalendarStylesContainer';
import { CalendarShowMorePopover } from './CalendarShowMorePopover';

import { CalendarMapper } from '../../mapper';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

const localizer = momentLocalizer(moment);

const DragAndDropCalendar = withDragAndDrop<CalendarEvent>(BigCalendar);

type CalendarComponentProps = Omit<CalendarProps<CalendarEvent>, 'localizer'> &
  Pick<withDragAndDropProps<CalendarEvent>, 'draggableAccessor' | 'resizableAccessor' | 'onEventDrop'>;

interface ShowMoreState {
  date: Date;
  anchorEl: HTMLElement;
  events: CalendarEvent[];
}

export const Calendar = ({ eventPropGetter, onEventDrop, ...props }: CalendarComponentProps) => {
  const theme = useTheme();

  const showMoreAnchorRef = useRef<HTMLElement | null>(null);
  const [showMore, setShowMore] = useState<Nullable<ShowMoreState>>(null);

  const defaultEventPropGetter: EventPropGetter<CalendarEvent> = event => ({
    style: {
      borderLeft: `3px solid ${getPriorityColor(event.priority)(theme)}`,
    },
  });

  const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    showMoreAnchorRef.current = (e.target as HTMLElement).closest('.rbc-show-more');
  };

  const handleShowMore = (events: CalendarEvent[], date: Date) => {
    const anchorEl = showMoreAnchorRef.current;
    if (!anchorEl) return;

    const hiddenCount = CalendarMapper.getHiddenCount(anchorEl, events.length);
    const hiddenEvents = events.slice(Math.max(0, events.length - hiddenCount));

    setShowMore({ events: hiddenEvents, date, anchorEl });
  };

  const handleSelectEvent = (event: CalendarEvent, e: MouseEvent<HTMLElement>) => {
    props.onSelectEvent?.(event, e);
    setShowMore(null);
  };

  const handleEventDrop: withDragAndDropProps<CalendarEvent>['onEventDrop'] = args => {
    setShowMore(null);
    onEventDrop?.(args);
  };

  const eventComponent = props.components?.event as ComponentType<{ event: CalendarEvent }> | undefined;

  return (
    <CalendarWrapper onClickCapture={handleClickCapture}>
      <DragAndDropCalendar
        {...props}
        popup={false}
        endAccessor="end"
        localizer={localizer}
        startAccessor="start"
        onShowMore={handleShowMore}
        onEventDrop={handleEventDrop}
        dayLayoutAlgorithm="no-overlap"
        eventPropGetter={eventPropGetter ?? defaultEventPropGetter}
        scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
      />

      <CalendarShowMorePopover
        date={showMore?.date ?? null}
        events={showMore?.events ?? []}
        EventComponent={eventComponent}
        anchorEl={showMore?.anchorEl ?? null}
        onClose={() => setShowMore(null)}
        onSelectEvent={(event, e) => handleSelectEvent(event, e)}
      />
    </CalendarWrapper>
  );
};
